// Browser-side Web Push helpers. Everything that touches `navigator`,
// `ServiceWorker`, `Notification` or `PushManager` lives here so feature
// components stay declarative and SSR/non-secure-context guards are in one place.

import type { SubscribePayload } from '../features/push/push.api';

const SW_URL = '/sw.js';

export function isPushSupported(): boolean {
    return (
        typeof navigator !== 'undefined' &&
        'serviceWorker' in navigator &&
        typeof window !== 'undefined' &&
        'PushManager' in window &&
        'Notification' in window
    );
}

// iOS only delivers Web Push to a home-screen-installed PWA (16.4+), so the UI
// needs to detect iOS + whether we're already running standalone.
export function isIos(): boolean {
    if (typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent || '';
    const iOSDevice = /iphone|ipad|ipod/i.test(ua);
    // iPadOS 13+ reports as Mac; detect via touch points.
    const iPadOS = /macintosh/i.test(ua) && (navigator as Navigator & { maxTouchPoints?: number }).maxTouchPoints != null && (navigator as Navigator & { maxTouchPoints?: number }).maxTouchPoints! > 1;
    return iOSDevice || iPadOS;
}

export function isStandalonePWA(): boolean {
    if (typeof window === 'undefined') return false;
    const displayStandalone = window.matchMedia?.('(display-mode: standalone)').matches;
    const iosStandalone = (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
    return !!displayStandalone || !!iosStandalone;
}

export function notificationPermission(): NotificationPermission | 'unsupported' {
    if (typeof Notification === 'undefined') return 'unsupported';
    return Notification.permission;
}

// VAPID public keys are base64url; PushManager.subscribe wants a BufferSource.
// Build on an explicit ArrayBuffer so the inferred type is Uint8Array<ArrayBuffer>
// (a plain `new Uint8Array(n)` infers ArrayBufferLike, which lib.dom rejects as
// applicationServerKey).
function urlBase64ToUint8Array(base64String: string) {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const raw = window.atob(base64);
    const output = new Uint8Array(new ArrayBuffer(raw.length));
    for (let i = 0; i < raw.length; i += 1) output[i] = raw.charCodeAt(i);
    return output;
}

async function getRegistration(): Promise<ServiceWorkerRegistration> {
    const existing = await navigator.serviceWorker.getRegistration(SW_URL);
    if (existing) return existing;
    return navigator.serviceWorker.register(SW_URL, { scope: '/' });
}

export async function getCurrentSubscription(): Promise<PushSubscription | null> {
    if (!isPushSupported()) return null;
    const reg = await navigator.serviceWorker.getRegistration(SW_URL);
    if (!reg) return null;
    return reg.pushManager.getSubscription();
}

function toPayload(sub: PushSubscription): SubscribePayload {
    const json = sub.toJSON();
    return {
        endpoint: json.endpoint ?? sub.endpoint,
        keys: {
            p256dh: json.keys?.p256dh ?? '',
            auth: json.keys?.auth ?? '',
        },
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
    };
}

export class PushPermissionError extends Error {}

/**
 * Register the SW, request notification permission, and subscribe this device.
 * Throws PushPermissionError if the user blocks notifications. Returns the
 * payload the API expects.
 */
export async function subscribeThisDevice(vapidPublicKey: string): Promise<SubscribePayload> {
    if (!isPushSupported()) throw new Error('push_unsupported');

    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
        throw new PushPermissionError(permission);
    }

    const reg = await getRegistration();
    await navigator.serviceWorker.ready;

    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
        sub = await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
        });
    }
    return toPayload(sub);
}

/** Unsubscribe this device. Returns the endpoint that was removed (or null). */
export async function unsubscribeThisDevice(): Promise<string | null> {
    const sub = await getCurrentSubscription();
    if (!sub) return null;
    const endpoint = sub.endpoint;
    await sub.unsubscribe();
    return endpoint;
}
